import { isFraction, isPowerOperator } from "@corca/core";

import { getScalarRatioDivisor } from "./unitToSympyMap";

import type { NodeJson } from "@corca/data";

export type UnitResult = {
  text: string;
  placement: "prefix" | "postfix";
};

/** Incompatible units sentinel (e.g. $10 + €5). */
export const UNIT_CONFLICT = Symbol("UNIT_CONFLICT");

export type UnitExtractionResult = UnitResult | typeof UNIT_CONFLICT | null;

const ADDITIVE_OP_KEYS = new Set(["symbol-plus", "symbol-subtraction"]);
const DIVISION_OP_KEY = "symbol-division";

/**
 * Collect the unit inline-children arrays from every number node in the tree. Returned as raw
 * NodeJson children so callers resolve them structurally (via `unitInlineChildrenToSemantic`)
 * rather than round-tripping through a display string.
 */
export function collectUnitInlineChildren(node: NodeJson): NodeJson[][] {
  const result: NodeJson[][] = [];
  const walk = (n: NodeJson): void => {
    if (n.type === "number") {
      const unitChild = n.children.find((c) => c.type === "unit");
      const inlineChild = unitChild?.children.find((c) => c.type === "inline");
      if (inlineChild && inlineChild.children.length > 0) {
        result.push(inlineChild.children);
      }
    }
    for (const child of n.children) walk(child);
  };
  walk(node);
  return result;
}

function getSymbolKey(node: NodeJson): string | undefined {
  if (node.type !== "operatorSymbol") return undefined;
  const sym = node.children[0];
  if (!sym || sym.type !== "symbol") return undefined;
  return sym.props.key;
}

/** Serialize unit inline children to display text (e.g. "m/s²", "kg·m"). */
export function serializeUnitInlineToText(inlineChildren: NodeJson[]): string {
  let text = "";
  for (let i = 0; i < inlineChildren.length; i++) {
    const c = inlineChildren[i];
    if (c.type === "plainText") {
      text += c.props.value ?? "";
    } else if (c.type === "operatorSymbol") {
      const sym = c.children.find((s) => s.type === "symbol");
      text += sym?.props.value ?? "";
    } else if (isPowerOperator(c)) {
      const expInline = c.children[0];
      if (expInline?.type === "inline" && expInline.children.length === 1) {
        const expChild = expInline.children[0];
        if (expChild.type === "number") {
          const n = Number(expChild.props.value);
          if (Number.isInteger(n)) {
            text += exponentToSuperscript(n);
          }
        }
      }
    } else if (c.type === "variable" || c.type === "constant") {
      const sym = c.children.find((s) => s.type === "symbol");
      text += sym?.props.value ?? "";
    } else if (c.type === "number") {
      text += c.props.value ?? "";
    }
  }
  return text;
}

function extractUnitFromNode(node: NodeJson): UnitExtractionResult {
  if (node.type !== "unit") return null;

  const placement = node.props.placement ?? "postfix";
  const inlineChild = node.children.find((c) => c.type === "inline");
  if (!inlineChild) return null;

  const text = serializeUnitInlineToText(inlineChild.children);
  if (!text) return null;

  return { text, placement };
}

function getUnitFromNumberNode(node: NodeJson): UnitExtractionResult {
  if (node.type !== "number") return null;
  const unitChild = node.children.find((c) => c.type === "unit");
  if (!unitChild) return null;
  return extractUnitFromNode(unitChild);
}

function isAdditiveOp(node: NodeJson): boolean {
  const key = getSymbolKey(node);
  return key !== undefined && ADDITIVE_OP_KEYS.has(key);
}

function isDivisionOp(node: NodeJson): boolean {
  return getSymbolKey(node) === DIVISION_OP_KEY;
}

function isScalarRatio(u: UnitExtractionResult): boolean {
  return u !== null && u !== UNIT_CONFLICT && getScalarRatioDivisor(u.text) !== null;
}

function multiplyUnits(a: UnitExtractionResult, b: UnitExtractionResult): UnitExtractionResult {
  if (a === UNIT_CONFLICT || b === UNIT_CONFLICT) return UNIT_CONFLICT;
  if (!a && !b) return null;
  if (!a) return b;
  if (!b) return a;
  // Scalar ratios (%, ‰, ‱) are dimensionless: same symbol survives, otherwise it drops.
  if (isScalarRatio(a) && isScalarRatio(b)) return a.text === b.text ? a : null;
  if (isScalarRatio(a)) return b;
  if (isScalarRatio(b)) return a;
  if (a.text === b.text) return { text: `${a.text}\u00b2`, placement: a.placement };
  return { text: `${a.text}\u00b7${b.text}`, placement: "postfix" };
}

function getIntegerExponent(powerNode: NodeJson): number | null {
  const exponentInline = powerNode.children[0];
  if (!exponentInline || exponentInline.type !== "inline") return null;
  if (exponentInline.children.length !== 1) return null;
  const child = exponentInline.children[0];
  if (child.type !== "number") return null;
  const n = Number(child.props.value);
  return Number.isInteger(n) ? n : null;
}

function powerUnit(base: UnitExtractionResult, exponent: number): UnitExtractionResult {
  if (base === UNIT_CONFLICT) return UNIT_CONFLICT;
  if (!base) return null;
  if (exponent === 1) return base;
  if (exponent === 0) return null;
  return { text: `${base.text}${exponentToSuperscript(exponent)}`, placement: base.placement };
}

function exponentToSuperscript(n: number): string {
  const superscriptDigits = "⁰¹²³⁴⁵⁶⁷⁸⁹";
  const abs = Math.abs(n);
  const digits = String(abs)
    .split("")
    .map((d) => superscriptDigits[Number(d)])
    .join("");
  return n < 0 ? `⁻${digits}` : digits;
}

function divideUnits(num: UnitExtractionResult, den: UnitExtractionResult): UnitExtractionResult {
  if (num === UNIT_CONFLICT || den === UNIT_CONFLICT) return UNIT_CONFLICT;
  // Scalar ratio (%, ‰, ‱): ratio/ratio cancels, ratio/unit or unit/ratio drops the ratio side.
  if (isScalarRatio(num) && isScalarRatio(den)) return null;
  if (isScalarRatio(num)) return den ? divideUnits(null, den) : null;
  if (isScalarRatio(den)) return num;
  if (!num && !den) return null;
  if (!den) return num;
  if (!num) return { text: `1/${den.text}`, placement: "postfix" };
  if (num.text === den.text) return null;
  return { text: `${num.text}/${den.text}`, placement: "postfix" };
}

/**
 * Same-dimension display texts, ascending scale. Mixed additions within a group are
 * dimensionally valid — SymPy computes the exact conversion; the propagated text is
 * the larger unit (matching the wheel's simplify_units collapse target). Plain single
 * units only: exponent/compound texts (m², kg·m) stay outside and conflict.
 */
const CONVERTIBLE_UNIT_GROUPS: ReadonlyArray<readonly string[]> = [
  ["Da", "μg", "mg", "g", "lb", "kg", "t"],
  ["pm", "Å", "nm", "μm", "mm", "cm", "in", "dm", "ft", "yd", "m", "km", "mi", "nmi", "au", "ly"],
];

function mergeConvertibleUnits(a: UnitResult, b: UnitResult): UnitResult | null {
  for (const group of CONVERTIBLE_UNIT_GROUPS) {
    const indexA = group.indexOf(a.text);
    if (indexA === -1) continue;
    const indexB = group.indexOf(b.text);
    if (indexB === -1) return null;
    return indexA >= indexB ? a : b;
  }
  return null;
}

function addUnits(a: UnitExtractionResult, b: UnitExtractionResult): UnitExtractionResult {
  if (a === UNIT_CONFLICT || b === UNIT_CONFLICT) return UNIT_CONFLICT;
  // Same scalar ratio adds cleanly (10% + 19% = %); different ratios fall through to conflict.
  if (isScalarRatio(a) && isScalarRatio(b) && a && b && a.text === b.text) return a;
  if (!a && !b) return null;
  if (!a || !b) {
    return UNIT_CONFLICT;
  }
  if (a.text === b.text && a.placement === b.placement) return a;
  return mergeConvertibleUnits(a, b) ?? UNIT_CONFLICT;
}

/** Get labeled variable name from a variable/constant NodeJson. */
function getVariableName(node: NodeJson): string | null {
  if (node.type !== "variable" && node.type !== "constant") return null;
  const symbolNode = node.children.find((c) => c.type === "symbol");
  if (!symbolNode || symbolNode.type !== "symbol") return null;

  const baseName = symbolNode.props.value;
  if (!baseName) return null;

  const bottomLabels = symbolNode.children.filter(
    (n) => n.type === "label" && n.props.position === "right-bottom",
  );
  if (bottomLabels.length === 0) return baseName;

  const labelText = bottomLabels
    .flatMap((l) => l.children.filter((c) => c.type === "inline"))
    .map((inline) =>
      inline.children
        .map((c) => {
          if (c.type === "number") return c.props.value;
          if (c.type === "plainText") return c.props.value;
          if (c.type === "variable" || c.type === "constant") {
            const sym = c.children.find((s) => s.type === "symbol");
            return sym && sym.type === "symbol" ? sym.props.value : "";
          }
          return "";
        })
        .join(""),
    )
    .join("");

  return labelText ? `${baseName}_${labelText}` : baseName;
}

function extractFromFraction(
  node: NodeJson,
  varUnits: ReadonlyMap<string, UnitResult>,
): UnitExtractionResult {
  const [numInline, denInline] = node.children;
  if (!numInline || !denInline) return null;
  const numUnit = extractFromInline(numInline, varUnits);
  const denUnit = extractFromInline(denInline, varUnits);
  return divideUnits(numUnit, denUnit);
}

/** Find the inner inline node inside a group (handles group → matrix(1×1) → inline). */
function findGroupInline(node: NodeJson): NodeJson | null {
  const directInline = node.children.find((c) => c.type === "inline");
  if (directInline) return directInline;

  const matrix = node.children.find((c) => c.type === "matrix");
  if (matrix) {
    const matrixInline = matrix.children.find((c) => c.type === "inline");
    if (matrixInline) return matrixInline;
  }

  return null;
}

function extractFromGroup(
  node: NodeJson,
  varUnits: ReadonlyMap<string, UnitResult>,
): UnitExtractionResult {
  const inner = findGroupInline(node);
  if (inner) return extractFromInline(inner, varUnits);
  return null;
}

function extractFromInline(
  node: NodeJson,
  varUnits: ReadonlyMap<string, UnitResult>,
): UnitExtractionResult {
  if (node.type !== "inline") return null;
  const { children } = node;
  if (children.length === 0) return null;

  const terms: NodeJson[][] = [[]];

  for (const child of children) {
    if (isAdditiveOp(child)) {
      terms.push([]);
    } else {
      terms[terms.length - 1].push(child);
    }
  }

  const termUnits: UnitExtractionResult[] = terms.map((termNodes) => {
    let result: UnitExtractionResult = null;
    let nextIsDivision = false;
    // A scalar-ratio factor multiplied by any non-ratio factor (incl. a unitless number)
    // is consumed as a scalar, so the ratio suffix should not survive the term.
    let hasRatioFactor = false;
    let hasNonRatioFactor = false;

    for (let i = 0; i < termNodes.length; i++) {
      const n = termNodes[i];
      if (isDivisionOp(n)) {
        nextIsDivision = true;
      } else if (isPowerOperator(n)) {
        // Power operators are handled via lookahead from the base node; skip.
      } else {
        let nodeUnit: UnitExtractionResult = null;
        let isOperand = true;

        if (n.type === "number") {
          nodeUnit = getUnitFromNumberNode(n);
        } else if (n.type === "unit") {
          // Standalone unit sibling (e.g. `6/3125 N/m·Pa` — unit trails a fraction)
          nodeUnit = extractUnitFromNode(n);
        } else if (n.type === "variable" || n.type === "constant") {
          const varName = getVariableName(n);
          if (varName) nodeUnit = varUnits.get(varName) ?? null;
        } else if (isFraction(n)) {
          nodeUnit = extractFromFraction(n, varUnits);
        } else if (n.type === "group") {
          nodeUnit = extractFromGroup(n, varUnits);
        } else {
          // Multiplication operatorSymbols etc. are not operands; ignore for factor counting.
          isOperand = false;
        }

        if (isOperand) {
          if (isScalarRatio(nodeUnit)) hasRatioFactor = true;
          else hasNonRatioFactor = true;
        }

        // Peek ahead for power operator (mirrors buildSympyCode.ts logic)
        const nextNode = termNodes[i + 1];
        if (isPowerOperator(nextNode)) {
          const exp = getIntegerExponent(nextNode);
          if (exp !== null) {
            nodeUnit = powerUnit(nodeUnit, exp);
          }
          i += 1; // skip the power operator
        }

        if (nextIsDivision) {
          result = divideUnits(result, nodeUnit);
          nextIsDivision = false;
        } else {
          result = multiplyUnits(result, nodeUnit);
        }
      }
    }

    if (isScalarRatio(result) && hasRatioFactor && hasNonRatioFactor) {
      return null;
    }

    return result;
  });

  if (termUnits.length === 0) return null;
  if (termUnits.some((t) => t === UNIT_CONFLICT)) return UNIT_CONFLICT;

  let combined: UnitExtractionResult = termUnits[0];
  for (let i = 1; i < termUnits.length; i++) {
    combined = addUnits(combined, termUnits[i]);
    if (combined === UNIT_CONFLICT) return UNIT_CONFLICT;
  }

  return combined;
}

/** True if tree contains a variable/constant with no entry in `varUnits` (treated as wildcard for dimensional checks). */
export function hasUnknownUnitVariable(
  node: NodeJson,
  varUnits: ReadonlyMap<string, UnitResult>,
): boolean {
  if (node.type === "variable" || node.type === "constant") {
    const varName = getVariableName(node);
    if (varName && !varUnits.has(varName)) return true;
  }
  return node.children.some((c) => hasUnknownUnitVariable(c, varUnits));
}

export function extractUnits(
  expr: NodeJson,
  varUnits: ReadonlyMap<string, UnitResult> = new Map(),
): UnitExtractionResult {
  if (expr.type !== "inline") return null;

  return extractFromInline(expr, varUnits);
}

/** Build variable → unit map from prior assignments (e.g. `d = 10km` → `"d"` → `"km"`). */
export function collectVariableUnits(
  priorAssignments: ReadonlyArray<{ variableName: string; rhsNodes: NodeJson[] }>,
): Map<string, UnitResult> {
  const varUnits = new Map<string, UnitResult>();

  for (const { variableName, rhsNodes } of priorAssignments) {
    const rhsInline: NodeJson = {
      type: "inline",
      props: {},
      children: rhsNodes,
    };
    const unit = extractUnits(rhsInline, varUnits);
    if (unit && unit !== UNIT_CONFLICT) {
      varUnits.set(variableName, unit);
    }
  }

  return varUnits;
}

function makeUnitNode(unit: UnitResult): NodeJson {
  return {
    type: "unit",
    props: { placement: unit.placement },
    children: [
      {
        type: "inline",
        props: {},
        children: [{ type: "plainText", props: { value: unit.text }, children: [] }],
      },
    ],
  };
}

/** True if the node tree contains any unit nodes. */
function hasUnitNode(node: NodeJson): boolean {
  if (node.type === "unit") return true;
  return node.children.some(hasUnitNode);
}

export function attachUnitToResult(result: NodeJson, unit: UnitResult | null): NodeJson {
  if (!unit) return result;
  if (result.type !== "inline") return result;

  // Keep SymPy's units if present (e.g. N·m → J)
  if (hasUnitNode(result)) return result;

  // Attach TS-computed unit (non-SI path)
  if (result.children.length === 1 && result.children[0].type === "number") {
    const numNode = result.children[0];
    return {
      ...result,
      children: [{ ...numNode, children: [...numNode.children, makeUnitNode(unit)] }],
    };
  }

  // Fraction or compound expression: append unit at end
  return {
    ...result,
    children: [...result.children, makeUnitNode(unit)],
  };
}
